import { Component, ErrorInfo, ReactNode } from "react";
import { captureException } from "@/services/sentry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  /**
   * Catches JavaScript errors anywhere in the child component tree, logs those errors, and displays a fallback UI.
   * @example
   * componentDidCatch(new Error('Sample Error'), { componentStack: 'Sample Component Stack' })
   * Calls OpenReplay.issue and logs the error.
   * @param {Error} error - The error thrown by a descendant component.
   * @param {ErrorInfo} errorInfo - Information about the component stack that contains the thrown error.
   * @returns {void} Logs the error and sets the error state.
   **/
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });

    // Track error in Sentry
    try {
      captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        tags: {
          errorBoundary: "ErrorBoundary",
        },
      });
    } catch (sentryError) {
      console.warn("Failed to capture error in Sentry:", sentryError);
    }

    // Track error in OpenReplay
    if (window.OpenReplay && typeof window.OpenReplay.issue === "function") {
      try {
        window.OpenReplay.issue("React Error Boundary", {
          error: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          context: "error_boundary",
        });
      } catch (openReplayError) {
        console.warn("Failed to track error in OpenReplay:", openReplayError);
      }
    }

    console.error("Error caught by boundary:", error, errorInfo);
  }

  /**
   * A React component that serves as an error boundary to catch JavaScript errors anywhere in the child component tree, log those errors, and display a fallback UI instead of crashing the entire component tree.
   * @component
   * @example
   *   <ErrorBoundary fallback={<div>Custom fallback component</div>}>
   *     <MyComponent />
   *   </ErrorBoundary>
   * @prop {ReactNode} fallback - Optional custom fallback component to display when an error is caught.
   * @state {boolean} hasError - Indicates if an error has been caught within the component's child tree.
   * @lifecycleMethod componentDidCatch - Lifecycle method used to catch errors and update the component's state accordingly.
   * @lifecycleMethod render - Conditionally renders either the fallback UI, the default error message UI, or the component's children based on the presence of errors.
   **/
  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-lg font-medium text-gray-900">Something went wrong</h3>
              <p className="mt-2 text-sm text-gray-500">
                We're sorry, but something unexpected happened. Please try refreshing the page.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
